import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Card, C, Loader, PrimaryButton } from "@/components/district/kit";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { MagnifyingGlass, PencilSimple, Plus, Trash, MapPin, UsersThree, CircleNotch, WarningCircle, ArrowsClockwise } from "@phosphor-icons/react";

const BLANK = { name: "", email: "", member_id: "", phone: "" };

export default function AllocationManager() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [dlg, setDlg] = useState(null); // person dialog config
  const [zoneDlg, setZoneDlg] = useState(null); // { clubId, clubName, zoneId, agId, agOptions }
  const [zoneOff, setZoneOff] = useState([]);   // zones with their ZG/ZS/AGs
  const [zoDlg, setZoDlg] = useState(null);      // zone-officials editor
  const [form, setForm] = useState(BLANK);
  const [match, setMatch] = useState(null);
  const [syncing, setSyncing] = useState(false);

  const load = async () => {
    const [a, z] = await Promise.all([api.get("/district-setup/allocation"), api.get("/district-setup/zone-officials")]);
    setData(a.data); setZoneOff(z.data.zones || []);
  };
  useEffect(() => { load().catch((e) => { toast.error(e.response?.data?.detail || "Failed to load"); setData({ error: true }); }); }, []);
  const zoneOf = (zid) => zoneOff.find((z) => z.zone_id === zid);

  const clubs = useMemo(() => {
    const list = data?.clubs || [];
    const s = q.trim().toLowerCase();
    return s ? list.filter((c) => (c.club_name || "").toLowerCase().includes(s)
      || (c.zone_name || "").toLowerCase().includes(s)) : list;
  }, [data, q]);

  if (!data) return <Loader label="Loading allocation…" />;
  if (data.error) return <p className="p-6 text-[15px]" style={{ color: C.sub }}>This is available to district setup admins.</p>;

  const openPerson = (cfg) => { setMatch(null); setForm({ name: cfg.name || "", email: cfg.email || "", member_id: cfg.rotary_id || "", phone: cfg.phone || "" }); setDlg(cfg); };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const lookupMember = async (over = {}) => {
    const phone = over.phone ?? form.phone, email = over.email ?? form.email;
    if (!phone && !email) return;
    try {
      const r = await api.get("/district-setup/member-lookup", { params: { phone: phone || "", email: email || "" } });
      if (r.data.match) {
        const m = r.data.match; setMatch(m);
        setForm((f) => ({ ...f, member_id: m.rotary_id || f.member_id, name: f.name || m.name || "", phone: f.phone || m.mobile || "" }));
      } else setMatch(false);
    } catch { /* lookup is best-effort */ }
  };

  const savePerson = async () => {
    if (dlg.roleEditable && !(dlg.role || form.role || "").trim() && !form.roleName?.trim()) { toast.error("Enter a role name"); return; }
    setBusy(true);
    try {
      if (dlg.kind === "ps") {
        await api.post("/district-setup/allocation/president-secretary", {
          club_id: dlg.clubId, role: dlg.role, name: form.name, email: form.email, member_id: form.member_id, phone: form.phone });
      } else {
        await api.post("/district-setup/allocation/officer", {
          club_id: dlg.clubId, role: dlg.roleEditable ? (form.roleName || dlg.role) : dlg.role,
          name: form.name, email: form.email, member_id: form.member_id, phone: form.phone,
          granularity: dlg.scope === "district" ? "district" : "club",
          mode: dlg.mode, assignment_id: dlg.assignment_id });
      }
      toast.success("Saved"); setDlg(null); await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
    finally { setBusy(false); }
  };

  const removeRow = async (payload) => {
    if (!window.confirm("Remove this assignment?")) return;
    try { await api.post("/district-setup/allocation/remove", payload); toast.success("Removed"); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const syncContacts = async () => {
    setSyncing(true);
    try {
      const r = await api.post("/district-setup/sync-contacts");
      toast.success(`Synced from repository · ${r.data.phones_filled} phone(s), ${r.data.member_ids_filled} Member ID(s) filled`);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Sync failed"); }
    finally { setSyncing(false); }
  };

  const saveZone = async () => {
    setBusy(true);
    try {
      const zid = zoneDlg.zoneId;
      if (!zid) { toast.error("Pick a zone"); setBusy(false); return; }
      const r = await api.post("/district-setup/assign-club-zone",
        { club_id: zoneDlg.clubId, zone_id: zid, ag_user_id: zoneDlg.agId || undefined });
      if (r.data.needs_ag_choice) {
        setZoneDlg({ ...zoneDlg, agOptions: r.data.ag_options });
        toast.message("Zone, ZG & ZS applied — pick the AG for this club");
      } else {
        toast.success("Zone & officials applied"); setZoneDlg(null); await load();
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const saveZoneOfficials = async () => {
    setBusy(true);
    try {
      await api.post("/district-setup/zone-officials", {
        zone_id: zoDlg.zone_id,
        zone_governor: zoDlg.zg?.email ? zoDlg.zg : null,
        zone_secretary: zoDlg.zs?.email ? zoDlg.zs : null,
        ags: (zoDlg.ags || []).filter((a) => a.email?.trim()),
      });
      toast.success("Zone officials saved"); setZoDlg(null); await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const RoleRow = ({ label, person, onSwap, onRemove, testid }) => (
    <div className="flex items-center justify-between gap-2 py-1.5" data-testid={testid}>
      <div className="min-w-0">
        <span className="text-[11px] font-bold uppercase tracking-wide" style={{ color: C.sub }}>{label}</span>
        <p className="truncate text-[14px] font-medium" style={{ color: person?.name ? C.ink : C.sub }}>
          {person?.name || <span className="italic">— vacant</span>}
          {person?.unmatched && <span title="Not matched to a member" className="ml-1"><WarningCircle size={13} weight="fill" style={{ display: "inline", color: "#C8102E" }} /></span>}
        </p>
        {person?.rotary_id && <p className="truncate text-[12px]" style={{ color: C.sub }}>ID: {person.rotary_id}</p>}
        {person?.email && <p className="truncate text-[12px]" style={{ color: C.sub }}>{person.email}</p>}
        {person?.phone && <p className="truncate text-[12px]" style={{ color: C.sub }}>{person.phone}</p>}
      </div>
      <div className="flex shrink-0 gap-1">
        <button data-testid={`${testid}-swap`} onClick={onSwap} className="grid h-8 w-8 place-items-center rounded-lg border" style={{ borderColor: C.line, color: "#4B31C9" }} title="Swap member"><PencilSimple size={15} weight="bold" /></button>
        {onRemove && <button data-testid={`${testid}-remove`} onClick={onRemove} className="grid h-8 w-8 place-items-center rounded-lg border" style={{ borderColor: C.line, color: C.cranberry }} title="Remove"><Trash size={14} weight="bold" /></button>}
      </div>
    </div>
  );

  const ZPerson = ({ label, p }) => (
    <div className="mt-1.5">
      <span className="text-[11px] font-bold uppercase tracking-wide" style={{ color: C.sub }}>{label}</span>
      <p className="text-[13px] font-medium" style={{ color: p?.name ? C.ink : C.sub }}>{p?.name || "—"}</p>
      {p && (p.rotary_id || p.phone) && (
        <p className="text-[11.5px]" style={{ color: C.sub }}>{p.rotary_id ? `ID: ${p.rotary_id}` : ""}{p.rotary_id && p.phone ? " · " : ""}{p.phone || ""}</p>
      )}
    </div>
  );

  return (
    <div className="mx-auto w-full max-w-[1200px] space-y-6" data-testid="allocation-manager">
      <button data-testid="back-to-setup" onClick={() => navigate("/setup")} className="inline-flex items-center gap-1.5 text-[14px] font-semibold" style={{ color: "#4B31C9" }}>
        <span aria-hidden>←</span> Back to GOV settings
      </button>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight sm:text-4xl" style={{ color: C.ink }}>Allocation Manager</h1>
          <p className="mt-1 text-[15px]" style={{ color: C.sub }}>Tweak your structure without re-uploading — swap members, add roles, change zones.</p>
        </div>
        <button data-testid="sync-contacts" onClick={syncContacts} disabled={syncing}
          className="inline-flex items-center gap-1.5 rounded-xl border px-3.5 py-2 text-[13.5px] font-semibold disabled:opacity-60"
          style={{ borderColor: C.line, color: "#1E7A46" }} title="Fill missing phone / email / Member ID from the member repository">
          <ArrowsClockwise size={16} weight="bold" className={syncing ? "animate-spin" : ""} /> {syncing ? "Syncing…" : "Sync contacts from repository"}
        </button>
      </div>

      {/* District team */}
      <Card data-testid="district-team">
        <div className="mb-2 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2"><UsersThree size={20} weight="bold" style={{ color: "#4B31C9" }} />
            <h2 className="text-[18px] font-bold" style={{ color: C.ink }}>District team</h2></div>
          <button data-testid="add-district-role" onClick={() => openPerson({ scope: "district", mode: "add", roleEditable: true, kind: "officer", title: "Add district official" })}
            className="inline-flex items-center gap-1 rounded-lg border px-3 py-1.5 text-[13px] font-semibold" style={{ borderColor: C.line, color: "#4B31C9" }}><Plus size={14} weight="bold" /> Add district official</button>
        </div>
        <div className="grid gap-x-6 sm:grid-cols-2 lg:grid-cols-3">
          {(data.district_team || []).map((tm) => (
            <RoleRow key={tm.assignment_id} label={tm.role} person={tm} testid={`team-${tm.assignment_id}`}
              onSwap={() => openPerson({ scope: "district", mode: "swap", role: tm.role, roleEditable: true, kind: "officer", assignment_id: tm.assignment_id, name: tm.name, email: tm.email, phone: tm.phone, rotary_id: tm.rotary_id })}
              onRemove={() => removeRow({ assignment_id: tm.assignment_id })} />
          ))}
          {(data.district_team || []).length === 0 && <p className="text-[14px]" style={{ color: C.sub }}>No district-team roles yet.</p>}
        </div>
      </Card>

      {/* Zone officials — set each zone's ZG, ZS and AG list; clubs inherit on assignment */}
      <Card data-testid="zone-officials">
        <div className="mb-2 flex items-center gap-2"><MapPin size={20} weight="bold" style={{ color: "#1E7A46" }} />
          <h2 className="text-[18px] font-bold" style={{ color: C.ink }}>Zone officials</h2></div>
        <p className="mb-3 text-[13px]" style={{ color: C.sub }}>Set each zone's Zone Governor, Zone Secretary and Assistant Governors. Clubs inherit these automatically when you assign them to the zone.</p>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {zoneOff.map((z) => (
            <div key={z.zone_id} className="rounded-xl border p-3" style={{ borderColor: C.line }} data-testid={`zone-off-${z.zone_id}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="text-[14px] font-bold" style={{ color: C.ink }}>{z.name}</span>
                <button data-testid={`zone-off-edit-${z.zone_id}`} onClick={() => setZoDlg({ zone_id: z.zone_id, name: z.name, zg: { name: z.zone_governor?.name || "", email: z.zone_governor?.email || "", phone: z.zone_governor?.phone || "", member_id: z.zone_governor?.rotary_id || "" }, zs: { name: z.zone_secretary?.name || "", email: z.zone_secretary?.email || "", phone: z.zone_secretary?.phone || "", member_id: z.zone_secretary?.rotary_id || "" }, ags: (z.ags || []).map((a) => ({ name: a.name || "", email: a.email || "", phone: a.phone || "", member_id: a.rotary_id || "" })) })}
                  className="grid h-7 w-7 place-items-center rounded-lg border" style={{ borderColor: C.line, color: "#4B31C9" }}><PencilSimple size={13} weight="bold" /></button>
              </div>
              <ZPerson label="Zone Governor" p={z.zone_governor} />
              <ZPerson label="Zone Secretary" p={z.zone_secretary} />
              <div className="mt-1.5">
                <span className="text-[11px] font-bold uppercase tracking-wide" style={{ color: C.sub }}>Assistant Governors</span>
                {(z.ags || []).length ? z.ags.map((a, i) => (
                  <div key={i} className="mt-0.5" data-testid={`zone-ag-${z.zone_id}-${i}`}>
                    <p className="text-[13px] font-medium" style={{ color: C.ink }}>{a.name}</p>
                    {(a.rotary_id || a.phone) && <p className="text-[11.5px]" style={{ color: C.sub }}>{a.rotary_id ? `ID: ${a.rotary_id}` : ""}{a.rotary_id && a.phone ? " · " : ""}{a.phone || ""}</p>}
                  </div>
                )) : <p className="text-[12px]" style={{ color: C.sub }}>—</p>}
              </div>
            </div>
          ))}
          {zoneOff.length === 0 && <p className="text-[14px]" style={{ color: C.sub }}>No zones yet — set a club's zone first.</p>}
        </div>
      </Card>
      <div className="sticky top-0 z-10 -mx-1 bg-white/95 px-1 py-2 backdrop-blur">
        <div className="relative">
          <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: C.sub }} />
          <input data-testid="club-search" autoFocus value={q} onChange={(e) => setQ(e.target.value)}
            placeholder={`Search ${(data.clubs || []).length} clubs by name or zone…`}
            className="w-full rounded-xl border py-3 pl-10 pr-3 text-[15px] outline-none focus:border-[#7C4DFF]" style={{ borderColor: C.line, color: C.ink }} />
        </div>
        {q.trim() && (
          <p className="mt-1.5 px-1 text-[12.5px]" style={{ color: C.sub }} data-testid="club-search-count">
            {clubs.length} {clubs.length === 1 ? "match" : "matches"} for “{q.trim()}”
          </p>
        )}
      </div>

      {/* Search-first: show a club's card only when searching, so you never scroll through them all */}
      {!q.trim() ? (
        <div className="grid place-items-center rounded-2xl border-2 border-dashed py-16 text-center" style={{ borderColor: C.line }} data-testid="club-search-empty">
          <MagnifyingGlass size={30} weight="bold" style={{ color: C.sub }} />
          <p className="mt-3 text-[15px] font-semibold" style={{ color: C.ink }}>Search for a club to edit</p>
          <p className="mt-1 max-w-sm text-[13.5px]" style={{ color: C.sub }}>Type a club name or zone above to open its card — swap President/Secretary, change zone, or set an AG. ({(data.clubs || []).length} clubs in your district)</p>
        </div>
      ) : (
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {clubs.map((c) => (
          <Card key={c.club_id} data-testid={`club-card-${c.club_id}`}>
            <div className="mb-2 flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="text-[10px] font-bold uppercase tracking-widest" style={{ color: "#C8102E" }}>Rotary</p>
                <h3 className="truncate text-[17px] font-bold tracking-tight" style={{ color: C.ink }}>{c.club_name}</h3>
              </div>
              <button data-testid={`zone-edit-${c.club_id}`} onClick={() => { const z = (data.zones || []).find((z) => z.name === c.zone_name); setZoneDlg({ clubId: c.club_id, clubName: c.club_name, zoneId: z?.zone_id || "", agId: "" }); }}
                className="inline-flex shrink-0 items-center gap-1 rounded-full border px-2.5 py-1 text-[12px] font-semibold" style={{ borderColor: C.line, color: c.zone_name ? "#1E7A46" : C.sub }} title="Change zone">
                <MapPin size={13} weight="bold" /> {c.zone_name || "Set zone"}
              </button>
            </div>
            <div className="divide-y" style={{ borderColor: C.line }}>
              <RoleRow label="President" person={c.president} testid={`pres-${c.club_id}`}
                onSwap={() => openPerson({ clubId: c.club_id, kind: "ps", role: "President", clubName: c.club_name, name: c.president?.name, email: c.president?.email, rotary_id: c.president?.rotary_id, phone: c.president?.phone, title: "Swap President" })}
                onRemove={c.president ? () => removeRow({ kind: "ps", club_id: c.club_id, role: "president" }) : null} />
              <RoleRow label="Secretary" person={c.secretary} testid={`sec-${c.club_id}`}
                onSwap={() => openPerson({ clubId: c.club_id, kind: "ps", role: "Secretary", clubName: c.club_name, name: c.secretary?.name, email: c.secretary?.email, rotary_id: c.secretary?.rotary_id, phone: c.secretary?.phone, title: "Swap Secretary" })}
                onRemove={c.secretary ? () => removeRow({ kind: "ps", club_id: c.club_id, role: "secretary" }) : null} />
              {(c.officers || []).map((o) => (
                <RoleRow key={o.assignment_id} label={o.role} person={o} testid={`off-${o.assignment_id}`}
                  onSwap={() => openPerson({ clubId: c.club_id, scope: "club", mode: "swap", kind: "officer", role: o.role, roleEditable: true, clubName: c.club_name, assignment_id: o.assignment_id, name: o.name, email: o.email, phone: o.phone, rotary_id: o.rotary_id, title: `Swap ${o.role}` })}
                  onRemove={() => removeRow({ assignment_id: o.assignment_id })} />
              ))}
            </div>
            <button data-testid={`add-role-${c.club_id}`} onClick={() => openPerson({ clubId: c.club_id, scope: "club", mode: "add", kind: "officer", roleEditable: true, clubName: c.club_name, title: "Add role to this club" })}
              className="mt-2 inline-flex items-center gap-1 rounded-lg border px-3 py-1.5 text-[13px] font-semibold" style={{ borderColor: C.line, color: "#4B31C9" }}><Plus size={14} weight="bold" /> Add role</button>
          </Card>
        ))}
        {clubs.length === 0 && <p className="text-[15px]" style={{ color: C.sub }}>No clubs match your search.</p>}
      </div>
      )}

      {/* Person dialog */}
      <Dialog open={!!dlg} onOpenChange={(v) => { if (!v) setDlg(null); }}>
        <DialogContent data-testid="person-dialog">
          <DialogHeader><DialogTitle>{dlg?.roleLabel || (dlg?.role ? `${dlg.role}${dlg?.clubName ? " · " + dlg.clubName : ""}` : (dlg?.title || "Assign member"))}</DialogTitle></DialogHeader>
          <div className="grid gap-3">
            {dlg?.roleEditable && (
              <label><span className="text-[13px] font-semibold" style={{ color: C.sub }}>Role name *</span>
                <input data-testid="person-role" defaultValue={dlg.role || ""} onChange={(e) => setForm({ ...form, roleName: e.target.value })}
                  placeholder="e.g. Assistant Governor" className="mt-1 w-full rounded-xl border px-3 py-2.5 text-[15px] outline-none focus:border-[#7C4DFF]" style={{ borderColor: C.line, color: C.ink }} /></label>
            )}
            <label><span className="text-[13px] font-semibold" style={{ color: C.sub }}>Full name</span>
              <input data-testid="person-name" value={form.name} onChange={set("name")} className="mt-1 w-full rounded-xl border px-3 py-2.5 text-[15px] outline-none focus:border-[#7C4DFF]" style={{ borderColor: C.line, color: C.ink }} /></label>
            <label><span className="text-[13px] font-semibold" style={{ color: C.sub }}>Email {dlg?.kind !== "ps" && "*"}</span>
              <input data-testid="person-email" value={form.email} onChange={set("email")} onBlur={() => lookupMember()} className="mt-1 w-full rounded-xl border px-3 py-2.5 text-[15px] outline-none focus:border-[#7C4DFF]" style={{ borderColor: C.line, color: C.ink }} /></label>
            <label><span className="text-[13px] font-semibold" style={{ color: C.sub }}>Phone number</span>
              <input data-testid="person-phone" value={form.phone} onChange={set("phone")} onBlur={() => lookupMember()} placeholder="e.g. +91 98765 43210" className="mt-1 w-full rounded-xl border px-3 py-2.5 text-[15px] outline-none focus:border-[#7C4DFF]" style={{ borderColor: C.line, color: C.ink }} />
              <span className="mt-1 block text-[11.5px]" style={{ color: C.sub }}>Include country code, e.g. +91 for India.</span></label>
            <label><span className="text-[13px] font-semibold" style={{ color: form.member_id ? C.sub : "#B45309" }}>{form.member_id ? "Rotary Member ID" : "Update Member ID"}</span>
              <input data-testid="person-member-id" value={form.member_id} onChange={set("member_id")} placeholder="Rotary Member ID" className="mt-1 w-full rounded-xl border px-3 py-2.5 text-[15px] outline-none focus:border-[#7C4DFF]" style={{ borderColor: C.line, color: C.ink }} /></label>
            {match && <p className="text-[12px] font-semibold" style={{ color: "#0F7B3F" }} data-testid="member-match">✓ Matched {match.name}{match.club_name ? ` · ${match.club_name}` : ""} — Member ID filled from the repository.</p>}
            {match === false && (form.phone || form.email) && <p className="text-[12px]" style={{ color: C.sub }} data-testid="member-nomatch">No member matched this phone/email — you can key the Member ID manually.</p>}
            {dlg?.kind === "ps" && <p className="text-[12px]" style={{ color: C.sub }}>Only President/Secretary are validated against the member repository. A missing/unmatched Member ID still assigns them — you can add it later.</p>}
          </div>
          <DialogFooter>
            <PrimaryButton data-testid="person-save" onClick={savePerson} disabled={busy}>
              {busy ? <CircleNotch size={16} className="animate-spin" /> : <PencilSimple size={16} weight="bold" />} Save
            </PrimaryButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Zone dialog — pick zone (club inherits ZG/ZS + AG), choose AG when several */}
      <Dialog open={!!zoneDlg} onOpenChange={(v) => { if (!v) setZoneDlg(null); }}>
        <DialogContent data-testid="zone-dialog">
          <DialogHeader><DialogTitle>Zone for {zoneDlg?.clubName}</DialogTitle></DialogHeader>
          <div className="grid gap-3">
            <label><span className="text-[13px] font-semibold" style={{ color: C.sub }}>Zone</span>
              <select data-testid="zone-select" value={zoneDlg?.zoneId || ""} onChange={(e) => setZoneDlg({ ...zoneDlg, zoneId: e.target.value, agId: "", agOptions: null })}
                className="mt-1 w-full rounded-xl border px-3 py-2.5 text-[15px] outline-none focus:border-[#7C4DFF]" style={{ borderColor: C.line, color: C.ink }}>
                <option value="">Select a zone…</option>
                {(data.zones || []).map((z) => <option key={z.zone_id} value={z.zone_id}>{z.name}</option>)}
              </select>
            </label>
            {(() => {
              const z = zoneDlg?.zoneId ? zoneOf(zoneDlg.zoneId) : null;
              const ags = zoneDlg?.agOptions || z?.ags || [];
              if (!z) return null;
              return (
                <>
                  <p className="text-[12.5px]" style={{ color: C.sub }}>Inherits <b>ZG:</b> {z.zone_governor?.name || "—"} · <b>ZS:</b> {z.zone_secretary?.name || "—"}</p>
                  {ags.length > 1 && (
                    <label><span className="text-[13px] font-semibold" style={{ color: C.sub }}>Assistant Governor (this zone has {ags.length}) *</span>
                      <select data-testid="zone-ag-select" value={zoneDlg?.agId || ""} onChange={(e) => setZoneDlg({ ...zoneDlg, agId: e.target.value })}
                        className="mt-1 w-full rounded-xl border px-3 py-2.5 text-[15px] outline-none focus:border-[#7C4DFF]" style={{ borderColor: C.line, color: C.ink }}>
                        <option value="">Choose an AG…</option>
                        {ags.map((a) => <option key={a.user_id} value={a.user_id}>{a.name}</option>)}
                      </select>
                    </label>
                  )}
                  {ags.length === 1 && <p className="text-[12.5px]" style={{ color: C.sub }}>AG: <b>{ags[0].name}</b> (only AG in this zone)</p>}
                  {ags.length === 0 && <p className="text-[12.5px]" style={{ color: "#B45309" }}>No AGs set for this zone yet — set them under “Zone officials”.</p>}
                </>
              );
            })()}
          </div>
          <DialogFooter>
            <PrimaryButton data-testid="zone-save" onClick={saveZone} disabled={busy || !zoneDlg?.zoneId || (((zoneDlg?.agOptions || zoneOf(zoneDlg?.zoneId)?.ags || []).length > 1) && !zoneDlg?.agId)}>
              {busy ? <CircleNotch size={16} className="animate-spin" /> : <MapPin size={16} weight="bold" />} Assign & inherit
            </PrimaryButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Zone-officials editor */}
      <Dialog open={!!zoDlg} onOpenChange={(v) => { if (!v) setZoDlg(null); }}>
        <DialogContent data-testid="zone-officials-dialog">
          <DialogHeader><DialogTitle>Officials for {zoDlg?.name}</DialogTitle></DialogHeader>
          <div className="grid max-h-[60vh] gap-3 overflow-y-auto pr-1">
            {["zg", "zs"].map((k) => (
              <div key={k} className="rounded-xl border p-3" style={{ borderColor: C.line }}>
                <p className="mb-1 text-[12px] font-bold uppercase tracking-wide" style={{ color: C.sub }}>{k === "zg" ? "Zone Governor" : "Zone Secretary"}</p>
                <input data-testid={`zo-${k}-name`} placeholder="Full name" value={zoDlg?.[k]?.name || ""} onChange={(e) => setZoDlg({ ...zoDlg, [k]: { ...zoDlg[k], name: e.target.value } })}
                  className="mb-2 w-full rounded-lg border px-3 py-2 text-[14px] outline-none focus:border-[#7C4DFF]" style={{ borderColor: C.line }} />
                <input data-testid={`zo-${k}-email`} placeholder="Email" value={zoDlg?.[k]?.email || ""} onChange={(e) => setZoDlg({ ...zoDlg, [k]: { ...zoDlg[k], email: e.target.value } })}
                  className="mb-2 w-full rounded-lg border px-3 py-2 text-[14px] outline-none focus:border-[#7C4DFF]" style={{ borderColor: C.line }} />
                <div className="flex gap-2">
                  <input data-testid={`zo-${k}-phone`} placeholder="Phone (e.g. +91 98765 43210)" value={zoDlg?.[k]?.phone || ""} onChange={(e) => setZoDlg({ ...zoDlg, [k]: { ...zoDlg[k], phone: e.target.value } })}
                    className="w-1/2 rounded-lg border px-3 py-2 text-[14px] outline-none focus:border-[#7C4DFF]" style={{ borderColor: C.line }} />
                  <input data-testid={`zo-${k}-member-id`} placeholder="Rotary Member ID" value={zoDlg?.[k]?.member_id || ""} onChange={(e) => setZoDlg({ ...zoDlg, [k]: { ...zoDlg[k], member_id: e.target.value } })}
                    className="w-1/2 rounded-lg border px-3 py-2 text-[14px] outline-none focus:border-[#7C4DFF]" style={{ borderColor: C.line }} />
                </div>
              </div>
            ))}
            <div className="rounded-xl border p-3" style={{ borderColor: C.line }}>
              <div className="mb-1 flex items-center justify-between">
                <p className="text-[12px] font-bold uppercase tracking-wide" style={{ color: C.sub }}>Assistant Governors</p>
                <button data-testid="zo-add-ag" onClick={() => setZoDlg({ ...zoDlg, ags: [...(zoDlg.ags || []), { name: "", email: "" }] })} className="inline-flex items-center gap-1 text-[13px] font-semibold" style={{ color: "#4B31C9" }}><Plus size={13} weight="bold" /> Add AG</button>
              </div>
              {(zoDlg?.ags || []).map((a, i) => (
                <div key={i} className="mb-2 flex flex-wrap gap-2" data-testid={`zo-ag-${i}`}>
                  <input placeholder="Name" value={a.name} onChange={(e) => { const ags = [...zoDlg.ags]; ags[i] = { ...ags[i], name: e.target.value }; setZoDlg({ ...zoDlg, ags }); }} className="min-w-0 flex-1 rounded-lg border px-2 py-2 text-[14px] outline-none focus:border-[#7C4DFF]" style={{ borderColor: C.line }} />
                  <input placeholder="Email" value={a.email} onChange={(e) => { const ags = [...zoDlg.ags]; ags[i] = { ...ags[i], email: e.target.value }; setZoDlg({ ...zoDlg, ags }); }} className="min-w-0 flex-1 rounded-lg border px-2 py-2 text-[14px] outline-none focus:border-[#7C4DFF]" style={{ borderColor: C.line }} />
                  <input data-testid={`zo-ag-${i}-phone`} placeholder="Phone" value={a.phone || ""} onChange={(e) => { const ags = [...zoDlg.ags]; ags[i] = { ...ags[i], phone: e.target.value }; setZoDlg({ ...zoDlg, ags }); }} className="w-[45%] rounded-lg border px-2 py-2 text-[14px] outline-none focus:border-[#7C4DFF]" style={{ borderColor: C.line }} />
                  <input data-testid={`zo-ag-${i}-member-id`} placeholder="Rotary Member ID" value={a.member_id || ""} onChange={(e) => { const ags = [...zoDlg.ags]; ags[i] = { ...ags[i], member_id: e.target.value }; setZoDlg({ ...zoDlg, ags }); }} className="w-[45%] rounded-lg border px-2 py-2 text-[14px] outline-none focus:border-[#7C4DFF]" style={{ borderColor: C.line }} />
                  <button onClick={() => setZoDlg({ ...zoDlg, ags: zoDlg.ags.filter((_, j) => j !== i) })} className="grid h-9 w-9 shrink-0 place-items-center rounded-lg border" style={{ borderColor: C.line, color: C.cranberry }}><Trash size={13} weight="bold" /></button>
                </div>
              ))}
              {(zoDlg?.ags || []).length === 0 && <p className="text-[13px]" style={{ color: C.sub }}>No AGs yet.</p>}
            </div>
          </div>
          <DialogFooter>
            <PrimaryButton data-testid="zo-save" onClick={saveZoneOfficials} disabled={busy}>
              {busy ? <CircleNotch size={16} className="animate-spin" /> : <PencilSimple size={16} weight="bold" />} Save officials
            </PrimaryButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
